import { useEffect, useMemo, useRef, useState } from 'react'
import * as RDialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Popover from '@radix-ui/react-popover'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, errorText } from '../lib/api'
import {
  keys, useAddComment, useAddSubtask, useDeleteSubtask, useRefreshTasks, useRenameSubtask,
  useSetSubtaskDone, useTags, useTask, useUpdateTask,
} from '../lib/queries'
import { renderMarkdown } from '../lib/markdown'
import type { Activity, Member, Priority, Status, Tag, TaskDetail, User } from '../lib/types'
import {
  PRIORITIES, PRIORITY_LABELS, formatBytes, formatDateTime, formatDue, formatRelative,
  handleFor, isOverdue, priorityMark, toDateInput,
} from '../lib/format'
import { TaskKey } from './TaskKey'
import { Avatar, Button, Input, Spinner, TagChip, Textarea, cx, useToast } from './ui'
import { ExportButton } from './ExportButton'

export function TaskDrawer({
  taskId, statuses, members, user, onClose,
}: {
  taskId: string
  statuses: Status[]
  members: Member[]
  user: User
  onClose: () => void
}) {
  const task = useTask(taskId)

  return (
    <RDialog.Root open onOpenChange={(open) => !open && onClose()}>
      <RDialog.Portal>
        <RDialog.Overlay className="fade-in fixed inset-0 z-40 bg-black/25" />
        <RDialog.Content
          className={cx(
            'drawer-in fixed inset-y-0 right-0 z-50 flex w-full max-w-[600px] flex-col',
            'border-l border-[var(--line)] bg-[var(--surface)] shadow-2xl shadow-black/20',
            'dark:shadow-black/60',
          )}
        >
          {task.isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <Spinner className="h-5 w-5 text-[var(--ink-3)]" />
            </div>
          )}
          {task.isError && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-[14px] text-[var(--ink-2)]">This task is not available.</p>
              <Button onClick={onClose}>Close</Button>
            </div>
          )}
          {task.data && (
            <TaskBody
              task={task.data}
              statuses={statuses}
              members={members}
              user={user}
              onClose={onClose}
            />
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}

function TaskBody({
  task, statuses, members, user, onClose,
}: {
  task: TaskDetail
  statuses: Status[]
  members: Member[]
  user: User
  onClose: () => void
}) {
  const update = useUpdateTask()
  const refresh = useRefreshTasks()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [title, setTitle] = useState(task.title)
  useEffect(() => setTitle(task.title), [task.id, task.title])

  function patch(body: Parameters<typeof api.updateTask>[1]) {
    update.mutate({ id: task.id, ...body }, {
      onError: (err) =>
        toast.show('That change did not save', {
          tone: 'error',
          detail: errorText(err),
        }),
    })
  }

  const watch = useMutation({
    mutationFn: (watching: boolean) => api.watchTask(task.id, watching),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.task(task.id) }),
  })

  const archive = useMutation({
    mutationFn: (archived: boolean) => api.archiveTask(task.id, archived),
    onSuccess: () => refresh(task.projectId, task.id),
  })

  const remove = useMutation({
    mutationFn: () => api.deleteTask(task.id),
    onSuccess: () => {
      refresh(task.projectId)
      onClose()
    },
    onError: (err) =>
      toast.show('Could not delete this task', {
        tone: 'error',
        detail: errorText(err),
      }),
  })

  const duplicate = useMutation({
    mutationFn: () => api.duplicateTask(task.id),
    onSuccess: (copy) => {
      refresh(task.projectId)
      toast.show(`Copied to ${copy.key}`)
    },
  })

  const status = statuses.find((s) => s.id === task.statusId)
  const drop = useFileDrop(task.id)

  return (
    // The whole drawer accepts files. A drop zone the size of the Attachments
    // heading is one people miss, and a missed drop used to make the browser
    // navigate away and open the file.
    <div
      data-file-drop
      className="relative flex min-h-0 flex-1 flex-col"
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      {drop.active && (
        <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-[var(--accent)] bg-[var(--accent-soft)]/80">
          <p className="text-[13px] font-medium text-[var(--accent)]">Drop to attach</p>
        </div>
      )}

      <header className="flex items-start gap-2 border-b border-[var(--line)] px-5 py-3.5">
        <div className="min-w-0 flex-1">
          {/*
            The key is the heading and the title sits under it. That is the
            reverse of the usual arrangement, and it is right here: SHOP-142 is
            the handle this team uses in Slack and commit messages, so it is
            the task's name, not its footnote.
          */}
          <div className="flex items-center gap-2">
            <RDialog.Title asChild>
              <h1>
                <TaskKey taskKey={task.key} size="heading" />
              </h1>
            </RDialog.Title>
            {priorityMark(task.priority) && (
              <span className="task-key text-[15px] font-bold text-[var(--danger)]">
                {priorityMark(task.priority)}
              </span>
            )}
            {task.archivedAt && (
              <span className="rounded border border-[var(--line)] bg-[var(--sunken)] px-1.5 py-px text-[11px] text-[var(--ink-3)]">
                Archived
              </span>
            )}
          </div>
          <RDialog.Description className="sr-only">
            Task details for {task.key}
          </RDialog.Description>

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== task.title && patch({ title: title.trim() })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') setTitle(task.title)
            }}
            aria-label="Task title"
            className={cx(
              'mt-1 w-full rounded border border-transparent bg-transparent px-1 py-0.5 -ml-1',
              'text-[16px] font-medium leading-snug outline-none transition-colors duration-[140ms]',
              'hover:border-[var(--line)] focus:border-[var(--accent)] focus:bg-[var(--surface)]',
            )}
          />
        </div>

        <div className="flex shrink-0 items-center gap-1 pt-1">
          <ExportButton taskIds={[task.id]} size="sm" label="Export" />

          <Button
            size="iconSm"
            variant="ghost"
            onClick={() => watch.mutate(!task.isWatching)}
            title={task.isWatching ? 'Stop watching this task' : 'Watch this task'}
            aria-pressed={task.isWatching}
            className={task.isWatching ? 'text-[var(--accent)]' : ''}
          >
            <EyeGlyph filled={task.isWatching} />
          </Button>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button size="iconSm" variant="ghost" aria-label="More actions">
                <DotsGlyph />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={5}
                className="fade-in z-50 w-[196px] rounded-lg border border-[var(--line)] bg-[var(--surface)] p-1 shadow-xl shadow-black/10 dark:shadow-black/50"
              >
                <MenuAction onSelect={() => duplicate.mutate()}>Duplicate task</MenuAction>
                <MenuAction onSelect={() => archive.mutate(!task.archivedAt)}>
                  {task.archivedAt ? 'Restore from archive' : 'Archive task'}
                </MenuAction>
                <DropdownMenu.Separator className="my-1 h-px bg-[var(--line)]" />
                <MenuAction
                  danger
                  onSelect={() => {
                    if (window.confirm(`Delete ${task.key} permanently? This cannot be undone.`)) {
                      remove.mutate()
                    }
                  }}
                >
                  Delete task
                </MenuAction>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          <RDialog.Close asChild>
            <Button size="iconSm" variant="ghost" aria-label="Close">
              <CloseGlyph />
            </Button>
          </RDialog.Close>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Properties
          task={task}
          status={status}
          statuses={statuses}
          members={members}
          user={user}
          onPatch={patch}
        />

        <Description task={task} onPatch={patch} members={members} />

        <Subtasks task={task} />

        <Attachments task={task} />

        <Comments task={task} user={user} members={members} />

        <ActivityLog activities={task.activities} />
      </div>
    </div>
  )
}

/**
 * Accepts files dropped anywhere on the task.
 *
 * `data-file-drop` on the container is what tells the global guard in main.tsx
 * to leave this subtree alone; everywhere else a stray file drop is swallowed
 * so the browser cannot navigate away from the app.
 */
function useFileDrop(taskId: string) {
  const [active, setActive] = useState(false)
  const depth = useRef(0)
  const queryClient = useQueryClient()
  const toast = useToast()

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadAttachment(taskId, file),
    onSuccess: (a) => {
      queryClient.invalidateQueries({ queryKey: keys.task(taskId) })
      toast.show(`Attached ${a.filename}`)
    },
    onError: (err) =>
      toast.show('Upload failed', {
        tone: 'error',
        detail: errorText(err),
      }),
  })

  return {
    active,
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return
      e.preventDefault()
      e.stopPropagation()
      depth.current = 1
      setActive(true)
    },
    // dragleave fires when crossing between children too, so the highlight is
    // cleared on a real exit rather than on every internal boundary.
    onDragLeave: (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
      depth.current = 0
      setActive(false)
    },
    onDrop: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return
      e.preventDefault()
      e.stopPropagation()
      setActive(false)
      for (const file of Array.from(e.dataTransfer.files)) upload.mutate(file)
    },
  }
}

// ------------------------------------------------------------- properties

function Properties({
  task, status, statuses, members, user, onPatch,
}: {
  task: TaskDetail
  status: Status | undefined
  statuses: Status[]
  members: Member[]
  user: User
  onPatch: (body: Parameters<typeof api.updateTask>[1]) => void
}) {
  const overdue = isOverdue(task)

  return (
    <dl className="grid grid-cols-[92px_1fr] items-center gap-x-3 gap-y-1 px-5 py-4 text-[13px]">
      <Row label="Status">
        <PickerMenu
          trigger={
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{ background: status?.color ?? 'var(--ink-3)' }}
              />
              {status?.name ?? '—'}
            </span>
          }
        >
          {statuses.map((s) => (
            <PickerItem
              key={s.id}
              selected={s.id === task.statusId}
              onSelect={() => onPatch({ statusId: s.id })}
            >
              <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              {s.name}
            </PickerItem>
          ))}
        </PickerMenu>
      </Row>

      <Row label="Assignee">
        <span className="flex min-w-0 items-center gap-2">
          {/* The picker's trigger is w-full, so without a shrink-wrapping
              parent it would take the row and shove the button to the margin. */}
          <span className="w-fit min-w-0">
            <PickerMenu
              trigger={
                <span className="flex items-center gap-1.5">
                  <Avatar user={task.assignee} size={18} />
                  {task.assignee?.name ?? <span className="text-[var(--ink-3)]">Unassigned</span>}
                </span>
              }
            >
              <PickerItem selected={!task.assignee} onSelect={() => onPatch({ assigneeId: null })}>
                <span className="h-[18px] w-[18px] rounded-full border border-dashed border-[var(--line-strong)]" />
                Unassigned
              </PickerItem>
              {members.map((m) => (
                <PickerItem
                  key={m.user.id}
                  selected={task.assignee?.id === m.user.id}
                  onSelect={() => onPatch({ assigneeId: m.user.id })}
                >
                  <Avatar user={m.user} size={18} />
                  {m.user.name}
                </PickerItem>
              ))}
            </PickerMenu>
          </span>

          {/* Picking yourself out of a menu you have to open first is the most
              common assignment there is, so it gets its own button. */}
          {task.assignee?.id !== user.id && (
            <button
              onClick={() => onPatch({ assigneeId: user.id })}
              className="shrink-0 whitespace-nowrap text-[11.5px] text-[var(--ink-3)] transition-colors duration-[140ms] hover:text-[var(--accent)]"
            >
              Assign to me
            </button>
          )}
        </span>
      </Row>

      <Row label="Priority">
        <PickerMenu
          trigger={
            <span className="flex items-center gap-1.5">
              {priorityMark(task.priority) && (
                <span className="task-key font-bold text-[var(--danger)]">
                  {priorityMark(task.priority)}
                </span>
              )}
              {PRIORITY_LABELS[task.priority]}
            </span>
          }
        >
          {PRIORITIES.map((p) => (
            <PickerItem
              key={p}
              selected={task.priority === p}
              onSelect={() => onPatch({ priority: p as Priority })}
            >
              <span className="task-key w-4 font-bold text-[var(--danger)]">
                {priorityMark(p)}
              </span>
              {PRIORITY_LABELS[p]}
            </PickerItem>
          ))}
        </PickerMenu>
      </Row>

      <Row label="Due">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={toDateInput(task.dueDate)}
            onChange={(e) => onPatch({ dueDate: e.target.value || null })}
            aria-label="Due date"
            className={cx(
              'rounded border border-transparent bg-transparent px-1 py-0.5 text-[13px] outline-none',
              'transition-colors duration-[140ms] hover:border-[var(--line)] focus:border-[var(--accent)]',
              overdue && 'text-[var(--danger)]',
            )}
          />
          {task.dueDate && (
            <span
              className={cx(
                'task-key text-[11px]',
                overdue ? 'font-semibold text-[var(--danger)]' : 'text-[var(--ink-3)]',
              )}
            >
              {formatDue(task.dueDate)}
            </span>
          )}
        </div>
      </Row>

      <Row label="Tags">
        <TagEditor task={task} onPatch={onPatch} />
      </Row>

      <Row label="Reporter">
        <span className="flex items-center gap-1.5 text-[var(--ink-2)]">
          <Avatar user={task.reporter} size={18} />
          {task.reporter?.name ?? 'Unknown'}
        </span>
      </Row>

      {task.watchers.length > 0 && (
        <Row label="Watching">
          <span className="flex flex-wrap items-center gap-1.5 text-[var(--ink-2)]">
            {task.watchers.map((w) => (
              <span key={w.id} className="flex items-center gap-1 text-[12px]">
                <Avatar user={w} size={16} />
                {w.name}
              </span>
            ))}
          </span>
        </Row>
      )}
    </dl>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-[12px] text-[var(--ink-3)]">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  )
}

function PickerMenu({
  trigger, children,
}: {
  trigger: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className={cx(
            'flex w-full items-center gap-1.5 rounded border border-transparent px-1 py-0.5 text-left',
            'transition-colors duration-[140ms] hover:border-[var(--line)] hover:bg-[var(--sunken)]',
          )}
        >
          {trigger}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="fade-in z-50 max-h-[300px] w-[212px] overflow-y-auto rounded-lg border border-[var(--line)] bg-[var(--surface)] p-1 shadow-xl shadow-black/10 dark:shadow-black/50"
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function PickerItem({
  selected, onSelect, children,
}: {
  selected: boolean
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cx(
        'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] outline-none',
        'transition-colors duration-[110ms] data-[highlighted]:bg-[var(--sunken)]',
        selected ? 'text-[var(--ink)]' : 'text-[var(--ink-2)]',
      )}
    >
      {children}
      {selected && <CheckGlyph className="ml-auto text-[var(--accent)]" />}
    </DropdownMenu.Item>
  )
}

function MenuAction({
  onSelect, danger, children,
}: {
  onSelect: () => void
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cx(
        'cursor-pointer rounded-md px-2 py-1.5 text-[12.5px] outline-none',
        'transition-colors duration-[110ms] data-[highlighted]:bg-[var(--sunken)]',
        danger ? 'text-[var(--danger)]' : 'text-[var(--ink-2)]',
      )}
    >
      {children}
    </DropdownMenu.Item>
  )
}

// -------------------------------------------------------------------- tags

function TagEditor({
  task, onPatch,
}: {
  task: TaskDetail
  onPatch: (body: Parameters<typeof api.updateTask>[1]) => void
}) {
  const tags = useTags()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')

  const selected = new Set(task.tags.map((t) => t.id))
  const available = (tags.data ?? []).filter((t) =>
    t.name.toLowerCase().includes(query.trim().toLowerCase()),
  )
  const exact = (tags.data ?? []).some(
    (t) => t.name.toLowerCase() === query.trim().toLowerCase(),
  )

  function apply(next: Tag[]) {
    onPatch({ tagIds: next.map((t) => t.id) })
  }

  const create = useMutation({
    mutationFn: () => api.createTag(query.trim(), '#64748b'),
    onSuccess: (tag) => {
      queryClient.invalidateQueries({ queryKey: keys.tags() })
      apply([...task.tags, tag])
      setQuery('')
    },
  })

  return (
    <div className="flex flex-wrap items-center gap-1">
      {task.tags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onRemove={() => apply(task.tags.filter((t) => t.id !== tag.id))}
        />
      ))}

      <Popover.Root onOpenChange={(open) => !open && setQuery('')}>
        <Popover.Trigger asChild>
          <button className="rounded border border-dashed border-[var(--line-strong)] px-1.5 py-px text-[11px] text-[var(--ink-3)] transition-colors duration-[140ms] hover:border-[var(--accent)] hover:text-[var(--ink-2)]">
            + tag
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={5}
            className="fade-in z-50 w-[216px] rounded-lg border border-[var(--line)] bg-[var(--surface)] p-1.5 shadow-xl shadow-black/10 dark:shadow-black/50"
          >
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find or create a tag"
              className="mb-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && query.trim() && !exact) {
                  e.preventDefault()
                  create.mutate()
                }
              }}
            />
            <div className="max-h-[220px] overflow-y-auto">
              {available.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() =>
                    apply(
                      selected.has(tag.id)
                        ? task.tags.filter((t) => t.id !== tag.id)
                        : [...task.tags, tag],
                    )
                  }
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-[var(--ink-2)] transition-colors duration-[110ms] hover:bg-[var(--sunken)]"
                >
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: tag.color }}
                  />
                  {tag.name}
                  {selected.has(tag.id) && <CheckGlyph className="ml-auto text-[var(--accent)]" />}
                </button>
              ))}

              {query.trim() && !exact && (
                <button
                  onClick={() => create.mutate()}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-[var(--accent)] hover:bg-[var(--sunken)]"
                >
                  Create “{query.trim()}”
                </button>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}

// ------------------------------------------------------------- description

function Description({
  task, onPatch, members,
}: {
  task: TaskDetail
  onPatch: (body: Parameters<typeof api.updateTask>[1]) => void
  members: Member[]
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(task.description)

  useEffect(() => setDraft(task.description), [task.id, task.description])

  const html = useMemo(() => renderMarkdown(task.description), [task.description])

  function save() {
    setEditing(false)
    if (draft !== task.description) onPatch({ description: draft })
  }

  return (
    <section className="border-t border-[var(--line)] px-5 py-4">
      <h2 className="mb-2 text-[12px] font-medium text-[var(--ink-3)]">Description</h2>

      {editing ? (
        <div className="space-y-2">
          <Textarea
            autoFocus
            rows={8}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setDraft(task.description)
                setEditing(false)
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save()
            }}
            placeholder={`Markdown works. Mention people with @${
              members[0] ? handleFor(members[0].user.email) : 'name'
            }.`}
            className="font-mono text-[12.5px]"
          />
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={save}>
              Save
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setDraft(task.description)
                setEditing(false)
              }}
            >
              Cancel
            </Button>
            <span className="task-key text-[11px] text-[var(--ink-3)]">⌘↵ to save</span>
          </div>
        </div>
      ) : task.description.trim() ? (
        <div
          onClick={() => setEditing(true)}
          className="prose-task cursor-text rounded-md px-1 py-0.5 -mx-1 text-[13px] transition-colors duration-[140ms] hover:bg-[var(--sunken)]"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="w-full rounded-md border border-dashed border-[var(--line-strong)] py-3 text-[12.5px] text-[var(--ink-3)] transition-colors duration-[140ms] hover:border-[var(--accent)] hover:text-[var(--ink-2)]"
        >
          Add a description
        </button>
      )}
    </section>
  )
}

// --------------------------------------------------------------- subtasks

/**
 * The task's checklist.
 *
 * Each item is a row of its own with its own id, not `- [ ]` inside the
 * description, so ticking one is a single request against a stable address.
 * That is what lets something other than this form — a script, or an agent
 * working through the task — report progress on one item without rewriting
 * anything else.
 */
function Subtasks({ task }: { task: TaskDetail }) {
  const [title, setTitle] = useState('')
  const toast = useToast()

  // All four patch the cache before they send, so a checklist can be worked
  // through as fast as it can be clicked.
  const add = useAddSubtask()
  const setDone = useSetSubtaskDone()
  const rename = useRenameSubtask()
  const remove = useDeleteSubtask()

  function failed(what: string) {
    return (err: unknown) => toast.show(what, { tone: 'error', detail: errorText(err) })
  }

  const done = task.subtasks.filter((s) => s.done).length
  const total = task.subtasks.length

  function submit() {
    const value = title.trim()
    if (!value) return
    add.mutate({ taskId: task.id, title: value }, { onError: failed('Could not add that item') })
    setTitle('') // cleared immediately: adding several in a row is the point
  }

  return (
    <section className="border-t border-[var(--line)] px-5 py-4">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-[12px] font-medium text-[var(--ink-3)]">Subtasks</h2>
        {total > 0 && (
          <>
            <span className="task-key text-[11px] text-[var(--ink-3)]">
              {done}/{total}
            </span>
            <span aria-hidden className="h-1 w-16 overflow-hidden rounded-full bg-[var(--line)]">
              <span
                className="block h-full rounded-full bg-[var(--accent)] transition-[width] duration-[240ms]"
                style={{ width: `${(done / total) * 100}%` }}
              />
            </span>
          </>
        )}
      </div>

      <ul className="space-y-0.5">
        {task.subtasks.map((sub) => (
          <li key={sub.id} className="group flex items-start gap-2 rounded px-1 py-1 -mx-1 hover:bg-[var(--sunken)]">
            <input
              type="checkbox"
              checked={sub.done}
              onChange={(e) =>
                setDone.mutate(
                  { id: sub.id, taskId: task.id, done: e.target.checked },
                  { onError: failed('Could not save that') },
                )
              }
              aria-label={sub.title}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-[var(--accent)]"
            />
            <input
              defaultValue={sub.title}
              onBlur={(e) => {
                const next = e.target.value.trim()
                if (next && next !== sub.title) {
                  rename.mutate({ id: sub.id, taskId: task.id, title: next },
                    { onError: failed('Could not rename that item') })
                }
                else e.target.value = sub.title
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
                if (e.key === 'Escape') {
                  e.currentTarget.value = sub.title
                  e.currentTarget.blur()
                }
              }}
              aria-label={`Rename ${sub.title}`}
              className={cx(
                'min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0 text-[13px]',
                'outline-none transition-colors duration-[140ms]',
                'hover:border-[var(--line)] focus:border-[var(--accent)] focus:bg-[var(--surface)]',
                sub.done && 'text-[var(--ink-3)] line-through decoration-[var(--line-strong)]',
              )}
            />
            <button
              onClick={() =>
                remove.mutate({ id: sub.id, taskId: task.id },
                  { onError: failed('Could not remove that item') })
              }
              aria-label={`Remove ${sub.title}`}
              className="shrink-0 rounded px-1 text-[13px] leading-none text-[var(--ink-3)] opacity-0 transition-opacity duration-[140ms] hover:text-[var(--danger)] group-hover:opacity-100"
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          }
          if (e.key === 'Escape') setTitle('')
        }}
        onBlur={submit}
        placeholder={total === 0 ? 'Add a subtask and press Enter' : 'Add another…'}
        aria-label="New subtask"
        className={cx(
          'mt-1 w-full rounded border border-dashed border-[var(--line-strong)] bg-transparent',
          'px-2 py-1.5 text-[13px] outline-none transition-colors duration-[140ms]',
          'placeholder:text-[var(--ink-3)] hover:border-[var(--line-strong)]',
          'focus:border-[var(--accent)] focus:border-solid focus:bg-[var(--surface)]',
        )}
      />
    </section>
  )
}

// ------------------------------------------------------------- attachments

function Attachments({ task }: { task: TaskDetail }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement>(null)

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadAttachment(task.id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.task(task.id) }),
    onError: (err) =>
      toast.show('Upload failed', {
        tone: 'error',
        detail: errorText(err),
      }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteAttachment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.task(task.id) }),
  })

  function send(files: FileList | File[] | null) {
    if (!files) return
    for (const file of Array.from(files)) upload.mutate(file)
  }

  return (
    // Drag-and-drop is handled by the drawer as a whole, so there is no narrow
    // strip to miss.
    <section className="border-t border-[var(--line)] px-5 py-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-[12px] font-medium text-[var(--ink-3)]">
          Attachments{task.attachments.length > 0 && ` (${task.attachments.length})`}
        </h2>
        <Button size="sm" variant="ghost" onClick={() => inputRef.current?.click()}>
          Add file
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            send(e.target.files)
            e.target.value = ''
          }}
        />
      </div>

      {task.attachments.length === 0 && !upload.isPending && (
        <p className="text-[12.5px] text-[var(--ink-3)]">
          Drop a file anywhere on this task, paste a screenshot into a comment,
          or use Add file.
        </p>
      )}

      <div className="space-y-1.5">
        {task.attachments.map((a) => {
          const isImage = a.contentType.startsWith('image/')
          return (
            <div key={a.id} className="group flex items-center gap-2">
              {isImage ? (
                <a href={api.attachmentUrl(a.id)} target="_blank" rel="noreferrer" className="block">
                  <img
                    src={api.attachmentUrl(a.id)}
                    alt={a.filename}
                    loading="lazy"
                    className="max-h-40 rounded-md border border-[var(--line)]"
                  />
                </a>
              ) : (
                <a
                  href={api.attachmentUrl(a.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-[var(--line)] px-2 py-1.5 text-[12.5px] transition-colors duration-[140ms] hover:border-[var(--line-strong)]"
                >
                  <span className="truncate">{a.filename}</span>
                  <span className="task-key ml-auto shrink-0 text-[11px] text-[var(--ink-3)]">
                    {formatBytes(a.sizeBytes)}
                  </span>
                </a>
              )}
              <button
                onClick={() => remove.mutate(a.id)}
                aria-label={`Remove ${a.filename}`}
                className="shrink-0 rounded p-1 text-[var(--ink-3)] opacity-0 transition-opacity duration-[140ms] hover:text-[var(--danger)] group-hover:opacity-100"
              >
                <CloseGlyph />
              </button>
            </div>
          )
        })}
        {upload.isPending && (
          <p className="flex items-center gap-2 text-[12.5px] text-[var(--ink-3)]">
            <Spinner /> Uploading…
          </p>
        )}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------- comments

function Comments({
  task, user, members,
}: {
  task: TaskDetail
  user: User
  members: Member[]
}) {
  const [body, setBody] = useState('')
  const add = useAddComment()
  const toast = useToast()
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteComment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.task(task.id) }),
  })

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadAttachment(task.id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.task(task.id) }),
  })

  async function submit() {
    const value = body.trim()
    if (!value) return
    try {
      await add.mutateAsync({ taskId: task.id, body: value })
      setBody('')
    } catch (err) {
      toast.show('Comment not posted', {
        tone: 'error',
        detail: errorText(err),
      })
    }
  }

  const hint = members.find((m) => m.user.id !== user.id) ?? members[0]

  return (
    <section className="border-t border-[var(--line)] px-5 py-4">
      <h2 className="mb-3 text-[12px] font-medium text-[var(--ink-3)]">
        Comments{task.comments.length > 0 && ` (${task.comments.length})`}
      </h2>

      <div className="space-y-4">
        {task.comments.map((c) => (
          <article key={c.id} className="group flex gap-2.5">
            <Avatar user={c.author} size={24} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-[12.5px] font-medium">{c.author?.name ?? 'Unknown'}</span>
                <time className="text-[11px] text-[var(--ink-3)]" dateTime={c.createdAt}>
                  {formatRelative(c.createdAt)}
                  {c.editedAt && ' · edited'}
                </time>
                {c.author?.id === user.id && (
                  <button
                    onClick={() => remove.mutate(c.id)}
                    className="ml-auto text-[11px] text-[var(--ink-3)] opacity-0 transition-opacity duration-[140ms] hover:text-[var(--danger)] group-hover:opacity-100"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div
                className="prose-task mt-0.5 text-[13px]"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(c.body) }}
              />
            </div>
          </article>
        ))}
      </div>

      <div className={cx('flex gap-2.5', task.comments.length > 0 && 'mt-4')}>
        <Avatar user={{ ...user }} size={24} className="mt-1" />
        <div className="min-w-0 flex-1 space-y-2">
          <Textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            // Pasting a screenshot straight into the comment box is how most
            // bug reports actually start.
            onPaste={(e) => {
              const file = Array.from(e.clipboardData.files)[0]
              if (file) {
                e.preventDefault()
                upload.mutate(file)
                toast.show('Screenshot attached')
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                submit()
              }
            }}
            placeholder={
              hint
                ? `Add a comment. Mention with @${handleFor(hint.user.email)}, paste to attach.`
                : 'Add a comment.'
            }
          />
          {body.trim() && (
            <div className="flex items-center gap-2">
              <Button variant="primary" size="sm" onClick={submit}>
                Comment
              </Button>
              <Button size="sm" onClick={() => setBody('')}>
                Cancel
              </Button>
              <span className="task-key text-[11px] text-[var(--ink-3)]">⌘↵ to post</span>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------- activity

function ActivityLog({ activities }: { activities: Activity[] }) {
  const [expanded, setExpanded] = useState(false)
  if (activities.length === 0) return null

  const shown = expanded ? activities : activities.slice(0, 5)

  return (
    <section className="border-t border-[var(--line)] px-5 py-4">
      <h2 className="mb-2 text-[12px] font-medium text-[var(--ink-3)]">Activity</h2>
      <ol className="space-y-1.5">
        {shown.map((a) => (
          <li key={a.id} className="flex items-baseline gap-2 text-[12px] text-[var(--ink-3)]">
            <span className="text-[var(--ink-2)]">{a.actor?.name ?? 'Someone'}</span>
            <span>{describe(a)}</span>
            <time className="task-key ml-auto shrink-0 text-[11px]" dateTime={a.createdAt}>
              {formatDateTime(a.createdAt)}
            </time>
          </li>
        ))}
      </ol>
      {activities.length > 5 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-[12px] text-[var(--ink-3)] hover:text-[var(--ink-2)]"
        >
          {expanded ? 'Show less' : `Show all ${activities.length}`}
        </button>
      )}
    </section>
  )
}

function describe(a: Activity): string {
  switch (a.kind) {
    case 'created':
      return 'created this task'
    case 'status_changed':
      return `moved it from ${a.oldValue || '—'} to ${a.newValue || '—'}`
    case 'assigned':
      return `assigned it to ${a.newValue || 'someone'}`
    case 'unassigned':
      return 'unassigned it'
    case 'title_changed':
      return 'changed the title'
    case 'description_changed':
      return 'edited the description'
    case 'priority_changed':
      return `changed priority to ${a.newValue}`
    case 'due_changed':
      return a.newValue ? `set the due date to ${a.newValue}` : 'cleared the due date'
    case 'tags_changed':
      return `changed tags to ${a.newValue || 'none'}`
    case 'commented':
      return 'commented'
    case 'attached':
      return `attached ${a.newValue}`
    case 'archived':
      return 'archived it'
    case 'restored':
      return 'restored it'
    default:
      return a.kind
  }
}

// ------------------------------------------------------------------ glyphs

const glyph = {
  width: 14, height: 14, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
}

function CloseGlyph() {
  return (
    <svg {...glyph} aria-hidden>
      <path d="m4 4 8 8M12 4l-8 8" />
    </svg>
  )
}

function DotsGlyph() {
  return (
    <svg {...glyph} aria-hidden>
      <circle cx="3.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  )
}

function EyeGlyph({ filled }: { filled: boolean }) {
  return (
    <svg {...glyph} aria-hidden>
      <path d="M1.5 8S3.9 3.5 8 3.5 14.5 8 14.5 8 12.1 12.5 8 12.5 1.5 8 1.5 8Z" />
      <circle cx="8" cy="8" r="2" fill={filled ? 'currentColor' : 'none'} />
    </svg>
  )
}

function CheckGlyph({ className }: { className?: string }) {
  return (
    <svg {...glyph} width={12} height={12} className={className} aria-hidden>
      <path d="m3.5 8.4 3 3 6-6.8" />
    </svg>
  )
}
